import React, { useEffect } from "react";
import { connect, ConnectedProps } from "react-redux";
import { PlusOutlined, EditOutlined } from "us.icons";
import { RootState } from "us.helper/types";
import {
  $Button,
  $Tooltip,
  $Typography,
  $Skeleton,
  $Message,
  $Empty,
} from "us.common/components";
import * as Actions from "actions";
import { useTranslation } from "react-i18next";
import { displayRuleExpression } from "functions";
import { CommonProperties } from "../CommonProperties";
import { Formik } from "formik";
const { Paragraph } = $Typography;

interface ITemplateRules {
  isXml?: boolean;
  onAddNewRule: () => void;
}

/**
 * @description - Template selection rule component for view and add rules.
 * @link Design Document -
 * @author Ishan Udyoga <ishanud@unicorn-solutions.com>
 * @since 11/09/2022
 */
const TemplateRules: React.FC<ITemplateRules & PropsFromRedux> = (props) => {
  const { isXml, templateRules, template, onAddNewRule, getTemplateRules, resetTemplateRules } =
    props;
  const { t } = useTranslation();

  const templateId = template.data.templateId;

  useEffect(() => {
    if (templateId) {
      getTemplateRules && getTemplateRules({ templateId });
    }else{
      resetTemplateRules && resetTemplateRules({});
    }
  }, [templateId]);

  const handleAddNewRule = () => {
    if (templateId && templateId > 0) {
      onAddNewRule();
    } else {
      // user have to save the template before adding the rules
      $Message.warning(
        t(
          "US.USC.TEMPLATEEDITOR:TEMPLATE_EDITOR.PLEASE_SAVE_THE_TEMPLATE_TO_ADD_A_NEW_TEMPLATE_RULE"
        )
      );
    }
  };

  return (
    <Formik
      initialValues={{}}
      enableReinitialize
      validateOnChange
      validateOnBlur
      onSubmit={() => {}}
    >
      {() => (
        <div>
          <div className="panel-title pr-1">
            <h4 className="d-flex align-items-center pl-2 py-2 m-0">
              {t("US.USC.TEMPLATEEDITOR:TEMPLATE_EDITOR.TEMPLATE_RULE")}
            </h4>
            <div className="d-flex align-items-center">
              {templateRules.data?.ruleExpressions?.length === 0 && (
                <$Tooltip
                  placement="right"
                  title={t("US.USC.TEMPLATEEDITOR:TEMPLATE_EDITOR.NEW_RULE")}
                >
                  <$Button
                    icon={<PlusOutlined />}
                    type="link"
                    size="small"
                    onClick={handleAddNewRule}
                  />
                </$Tooltip>
              )}
              {templateRules.data?.ruleExpressions.length > 0 && (
                <$Tooltip
                  placement="right"
                  title={t("US.USC.TEMPLATEEDITOR:TEMPLATE_EDITOR.EDIT_RULE")}
                >
                  <$Button
                    icon={<EditOutlined />}
                    type="link"
                    size="small"
                    onClick={onAddNewRule}
                    disabled={!templateId ? true : false}
                  />
                </$Tooltip>
              )}
            </div>
          </div>
          <div
            className="p-2 my-1"
            style={{ maxHeight: "60%", overflowY: "auto" }}
          >
            {templateRules.data?.ruleExpressions.length === 0 &&
              !templateRules.isLoading && (
                <$Empty
                  image={$Empty.PRESENTED_IMAGE_SIMPLE}
                  description={t("COMMON.NO_DATA")}
                />
              )}
            <$Skeleton
              loading={templateRules.isLoading}
              active
              paragraph={{
                rows: 2,
              }}
            >
              {templateRules.data?.ruleExpressions?.map((rule: string) => (
                <div
                  className="d-flex py-1 px-2 mb-2 editor-list-item rules numbering"
                  style={{ height: "auto" }}
                  onDoubleClick={onAddNewRule}
                >
                  <div
                    className="d-flex align-items-center pl-1"
                    style={{ maxWidth: "170px" }}
                  >
                    <Paragraph
                      ellipsis={{
                        rows: 3,
                        tooltip: displayRuleExpression(rule),
                      }}
                      className="text-muted"
                      style={{
                        marginBottom: 0,
                        whiteSpace: "normal",
                        maxWidth: "100%",
                      }}
                    >
                      {displayRuleExpression(rule)}
                    </Paragraph>
                  </div>
                </div>
              ))}
            </$Skeleton>
          </div>
          <div className="panel-title pr-1">
            <h4 className="d-flex align-items-center pl-2 py-2 m-0">
              {t("US.USC.TEMPLATEEDITOR:TEMPLATE_EDITOR.PROPERTIES")}
            </h4>
          </div>
          <CommonProperties isXml={isXml} />
        </div>
      )}
    </Formik>
  );
};

const mapStateToProps = (state: RootState) => {
  const { templateEditor, templatesHistory } = state;
  const { templateRules } = templateEditor;
  const { template } = templatesHistory;

  return {
    templateRules,
    template,
  };
};

const { templateRules } = Actions.templateEditor;

const mapDispatchToProps = {
  getTemplateRules: templateRules.get,
  resetTemplateRules: templateRules.reset,
};

const connector = connect(mapStateToProps, mapDispatchToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;

export default connector(TemplateRules);
